import { useCallback, useEffect, useState } from 'react';
import { getSessionsForDateRange } from '../services/sessions';
import type { Session } from '../types';

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function getSessionDate(session: Session): Date {
  if (session.startedAt?.toDate) return session.startedAt.toDate();
  return new Date((session.startedAt as unknown as { seconds: number }).seconds * 1000);
}

export function useTimelineSessions(
  userId: string | null,
  rangeStart: Date,
  rangeEndExclusive: Date
): { sessionsByDay: Map<string, Session[]>; loading: boolean; refetch: () => void } {
  const [sessionsByDay, setSessionsByDay] = useState<Map<string, Session[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!userId) {
        setSessionsByDay(new Map());
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const sessions = await getSessionsForDateRange(userId, rangeStart, rangeEndExclusive);
        if (cancelled) return;

        const grouped = new Map<string, Session[]>();
        for (const session of sessions) {
          const key = startOfDay(getSessionDate(session)).toISOString();
          if (!grouped.has(key)) grouped.set(key, []);
          grouped.get(key)!.push(session);
        }
        setSessionsByDay(grouped);
      } catch {
        if (!cancelled) {
          setSessionsByDay(new Map());
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [userId, rangeStart, rangeEndExclusive, fetchKey]);

  return { sessionsByDay, loading, refetch };
}
